import * as DialogPrimitive from "@radix-ui/react-dialog";
import { createRootRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  Boxes,
  HandCoins,
  Wallet,
  ClipboardList,
  Landmark,
  ShoppingCart,
  Menu,
  Package,
  PackagePlus,
  Settings,
  Smartphone,
  Search,
  Store,
  Tags,
  TrendingUp,
  Truck,
  Users,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { type CSSProperties, useEffect, useState } from "react";
import { Logo, LogoManzana } from "@/components/Logo";
import { BarraEstado } from "@/components/layout/BarraEstado";
import { RequiereSesion, rutaMontadaEsPublica } from "@/lib/auth-context";
import { BuscadorProvider, TeclaAtajo, useBuscador } from "@/lib/buscador-context";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { IconButton } from "@/components/ui/icon-button";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { alPausar } from "@/lib/api";
import { useConfig } from "@/lib/queries";
import { useHayVersionNueva } from "@/lib/version";

interface GrupoNav {
  titulo: string;
  items: ReadonlyArray<{ to: string; label: string; icon: LucideIcon }>;
}

const NAV: ReadonlyArray<GrupoNav> = [
  {
    titulo: "General",
    items: [{ to: "/", label: "Vender", icon: ShoppingCart }],
  },
  {
    titulo: "Productos",
    items: [
      { to: "/catalogo-interno", label: "Catálogo", icon: Package },
      { to: "/inventario", label: "Inventario", icon: Boxes },
      { to: "/proveedores", label: "Proveedores", icon: Truck },
      { to: "/listas-precios", label: "Listas de precios", icon: Tags },
      { to: "/compras", label: "Compras", icon: PackagePlus },
      { to: "/tienda", label: "Tienda online", icon: Store },
    ],
  },
  {
    titulo: "Comercial",
    items: [
      { to: "/ventas", label: "Ventas", icon: ClipboardList },
      { to: "/clientes", label: "Clientes", icon: Users },
    ],
  },
  {
    titulo: "Finanzas",
    items: [
      { to: "/caja", label: "Caja", icon: Wallet },
      { to: "/cheques", label: "Cheques", icon: Landmark },
      { to: "/ganancias", label: "Ganancias", icon: TrendingUp },
      { to: "/socios", label: "Socios", icon: HandCoins },
    ],
  },
  {
    titulo: "Laboratorio",
    items: [{ to: "/reparaciones", label: "Reparaciones", icon: Wrench }],
  },
  {
    titulo: "Sistema",
    items: [{ to: "/configuracion", label: "Configuración", icon: Settings }],
  },
];

const CANTIDAD_MODULOS = NAV.reduce((total, grupo) => total + grupo.items.length, 0);

/**
 * Lo que ocupa el sidebar en escritorio sin contar los modulos: el bloque del
 * logo (56), el padding del nav (16), una etiqueta de 18 por grupo, los gaps de
 * 2 debajo de cada etiqueta y entre modulos (uno por modulo) y los de 8 entre
 * grupos. Sale de `NAV`, asi un modulo o un grupo nuevo no descuadra la cuenta.
 */
const ALTO_FIJO_NAV = 56 + 16 + NAV.length * 18 + CANTIDAD_MODULOS * 2 + (NAV.length - 1) * 8;

const ALTO_MODULO = `clamp(2rem, calc((100dvh - ${ALTO_FIJO_NAV}px) / ${CANTIDAD_MODULOS}), 2.75rem)`;

function CuerpoSidebar({
  pathname,
  alNavegar,
  pie,
}: {
  pathname: string;
  alNavegar?: () => void;
  /** Lo que va al final de la lista (el tema, en el menú del celular). */
  pie?: React.ReactNode;
}): JSX.Element {
  return (
    <>
      {/*
        `h-14` fijo, el mismo que el header. Los dos bloques terminan en una
        linea horizontal que se lee como UNA sola cruzando la pantalla, y con
        alturas distintas —61px contra 53px— quedaba un escalon de 8px justo en
        lo primero que se mira.
      */}
      <div className="flex h-14 shrink-0 items-center border-b px-4">
        <Link
          to="/"
          onClick={alNavegar}
          aria-label="Ir al mostrador"
          className="block rounded-md transition-opacity duration-150 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Logo />
        </Link>
      </div>

      {/*
        `min-h-0` ademas de `flex-1`: sin eso un hijo flex no baja de su alto de
        contenido y el `overflow-y-auto` no llega a activarse nunca — la lista
        empuja el sidebar mas alto que la pantalla y termina scrolleando la
        pagina entera, que es justo lo que no queremos.

        Las cuentas, para la MacBook Air 13" en la que se usa esto (viewport de
        833px de alto): 56 del bloque del logo + 16 del padding + 6 etiquetas de
        grupo de 18 + 16 modulos de 32 + 32 de los gaps entre renglones + 5 gaps
        entre grupos de 8 = 764px. Entran los dieciseis sin scroll y sobran ~70
        de aire, que es el margen para la barra de favoritos del navegador.

        Con los modulos a 36px y los gaps de 12 la suma daba 848 —ya no entraba
        desde que se sumo Socios—. Por eso en escritorio el modulo baja a 32px,
        el alto de renglon de menu de sidebar estandar, que sigue siendo un
        blanco de clic comodo con mouse. En mobile (el drawer) se conservan los
        44px tactiles: ahi el scroll no molesta.

        Esos 32px son el PISO, no la medida. Con alto de sobra cada modulo se
        estira a su parte de lo que queda —el alto de la ventana menos lo fijo
        (`ALTO_FIJO_NAV`), repartido entre los modulos— hasta el techo de 44px.
        Asi el sidebar llena la pantalla en vez de dejar un hueco abajo, y en
        la Air, con la ventana que hay, queda en ~36px.
      */}
      <nav
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-2 md:gap-2"
        style={{ "--alto-modulo": ALTO_MODULO } as CSSProperties}
        aria-label="Modulos"
      >
        {NAV.map((grupo) => (
          <div key={grupo.titulo} className="flex flex-col gap-0.5">
            <div className="px-2 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground md:pb-0.5">
              {grupo.titulo}
            </div>
            {grupo.items.map((item) => {
              const Icono = item.icon;
              const activo = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={alNavegar}
                  aria-current={activo ? "page" : undefined}
                  className={cn(
                    "group flex min-h-11 items-center gap-2 rounded-md px-2 text-sm transition-colors duration-150 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-9 md:min-h-[var(--alto-modulo)]",
                    activo
                      ? "bg-accent font-medium text-accent-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  <Icono className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
        {pie}
      </nav>
    </>
  );
}

/**
 * Entrada al buscador desde el encabezado, visible en todas las pantallas.
 *
 * El atajo se ESCRIBE en el boton en vez de dejarlo escondido: un atajo que no
 * se anuncia no lo usa nadie, y este es el que resuelve la consulta mas
 * frecuente del dia. En pantallas chicas el texto se recorta a la lupa, porque
 * ahi no hay teclado y el atajo no aplica.
 */
function BotonBuscar(): JSX.Element {
  const { abrir } = useBuscador();

  return (
    <Button
      type="button"
      variant="outline"
      onClick={abrir}
      className={cn(
        // 44px en el celular (objetivo tactil) y `sm:h-9` en desktop, igual que
        // las tarjetas de `BarraEstado`: los dos bloques comparten la misma
        // linea del header y con alturas distintas la franja se ve desprolija
        // aunque cada pieza este bien.
        "px-2 font-normal text-muted-foreground hover:text-foreground sm:h-9",
        // En desktop se estira para tapar el hueco que quedaba entre el
        // buscador y los indicadores. Es lo mas usado del header: que se lleve
        // el espacio sobrante.
        "md:w-full md:justify-between md:px-3",
      )}
      aria-label="Buscar productos, clientes o equipos"
      aria-keyshortcuts="Meta+K Control+K"
    >
      <span className="flex items-center gap-2">
        <Search aria-hidden="true" />
        <span className="hidden md:inline">Buscar</span>
      </span>
      {/* `kbd` ya es mono por el preflight de Tailwind; no hace falta `font-mono`. */}
      <TeclaAtajo className="hidden rounded-sm border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground sm:inline-block" />
    </Button>
  );
}

/**
 * Las rutas que se dibujan SOLAS, sin sidebar ni header de app.
 *
 * El catalogo lo abre un cliente desde un link de WhatsApp. Envolverlo en el
 * panel le mostraria la navegacion interna del negocio —Caja, Cheques,
 * Proveedores— y, peor, el header con la cotizacion y el estado del local.
 *
 * El login y la instalacion NO estan aca: resuelven lo mismo tapando todo con
 * un overlay fijo, y funciona. No se tocan.
 */
const RUTAS_SIN_PANEL: readonly string[] = ["/catalogo"];

/**
 * Las rutas publicas (login, instalacion, catalogo) se dibujan sin el panel y
 * SIN montarlo: el panel trae la barra de estado y el buscador, que piden
 * cotizacion, deudas, proveedores y el taller. Sin sesion todo eso volvia 401,
 * y un visitante del catalogo disparaba pedidos del ERP. Ver `RequiereSesion`.
 */
function PantallaSuelta(): JSX.Element | null {
  const ruta = useRouterState({ select: (s) => s.location.pathname });
  // La ruta que el <Outlet/> va a dibujar de verdad. Mientras se redirige de
  // «/» a /login la direccion ya dice /login, pero el router todavia tiene
  // montada la ruta de antes: el <Outlet/> dibujaba el Mostrador sin el panel
  // y sin <BuscadorProvider>, y tiraba cuatro errores de React antes de llegar
  // al login. Una ruta protegida no se dibuja aca: se espera a que llegue.
  const publica = useRouterState({
    select: (s) => rutaMontadaEsPublica(s.matches, s.location.pathname),
  });
  if (!publica) return null;

  if (RUTAS_SIN_PANEL.includes(ruta)) {
    /*
      SIN PADDING Y SIN EL TEMA DE LA APP.
      ------------------------------------
      Esto no es una pantalla del ERP adentro de un marco: es una pagina que
      abre un cliente, y se dibuja de borde a borde. El `p-4` que habia le
      dejaba un marco de 16 px alrededor, y el `bg-background` lo pintaba del
      color del tema. En claro no se notaba —marco blanco sobre pagina
      blanca—, pero en un telefono en modo oscuro la pagina aparecia angosta,
      con un recuadro negro de los cuatro lados.

      El fondo lo pone la pagina, no el shell, y va tambien en el `body` para
      que el rebote del scroll en iOS no muestre el gris del tema.
    */
    return (
      <main className="min-h-[100dvh] bg-white text-[#0A0A0A]">
        <Outlet />
      </main>
    );
  }

  // El login y la instalacion se tapan solos con su overlay fijo.
  return <Outlet />;
}

/**
 * La ventana de la pausa (ver PAUSA en el backend). Tapa todo y no se cierra:
 * mientras se trabaja sobre los datos nadie tiene que cargar nada. Cuando la
 * pausa se levanta, recargar la pagina vuelve al sistema.
 */
function VentanaPausa(): JSX.Element | null {
  const [enPausa, setEnPausa] = useState(false);
  useEffect(() => alPausar(() => setEnPausa(true)), []);
  if (!enPausa) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="pausa-titulo"
      aria-describedby="pausa-texto"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6 text-center shadow-lg">
        <Logo className="justify-center" />
        <h2 id="pausa-titulo" className="text-lg font-semibold">
          Estamos trabajando en el sistema
        </h2>
        <p id="pausa-texto" className="text-sm text-muted-foreground">
          Por ahora no se puede cargar nada. Cuando terminemos, el sistema vuelve a andar.
        </p>
        <Button type="button" className="w-full" onClick={() => window.location.reload()}>
          Volver a intentar
        </Button>
      </div>
    </div>
  );
}

/** Debajo de `md` (768 px): el celular. Se escucha el cambio (girar el teléfono). */
function useEsCelular(): boolean {
  const consulta = "(max-width: 767px)";
  const [es, setEs] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(consulta).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(consulta);
    const alCambiar = (): void => setEs(mq.matches);
    mq.addEventListener("change", alCambiar);
    return () => mq.removeEventListener("change", alCambiar);
  }, []);
  return es;
}

/**
 * EL ENCABEZADO DEL CELULAR SE CORRE AL BAJAR
 * -------------------------------------------
 * En el celular la pantalla es lo que falta: al deslizar hacia abajo para leer
 * una lista, el encabezado se corre hacia arriba y le deja su renglón al
 * contenido; apenas se desliza hacia arriba, vuelve. Arriba de todo siempre se
 * ve. En la compu (md+) no se mueve nunca.
 *
 * En el celular el encabezado vive ADENTRO de `<main>`, pegado arriba
 * (`sticky`), y se esconde corriéndolo hacia arriba con `translateY`: no
 * cambia el alto de nada, así que el contenido no salta. La primera versión lo
 * escondía con un margen negativo, que agrandaba la zona que se desliza: la
 * lista saltaba 56 px de más (subir 30 px corría el renglón 86) y en las
 * páginas cortas parpadeaba al llegar al final.
 *
 * Mira el deslizamiento de `<main id="contenido">`, que es lo que se desliza:
 * la página entera no se mueve (el armazón mide el alto de la pantalla). Un
 * umbral de unos px evita que tiemble con el rebote de iOS.
 */
function useEncabezadoQueSeCorre(pathname: string): { corrido: boolean; mostrar: () => void } {
  const [corrido, setCorrido] = useState(false);
  // Al cambiar de pantalla vuelve a verse (se ajusta en el render y no en un
  // efecto, para no dibujar una vez de más).
  const [ruta, setRuta] = useState(pathname);
  if (ruta !== pathname) {
    setRuta(pathname);
    setCorrido(false);
  }

  useEffect(() => {
    const main = document.getElementById("contenido");
    if (!main) return;
    const compu = window.matchMedia("(min-width: 768px)");
    let ultimo = main.scrollTop;
    const alDeslizar = (): void => {
      const y = main.scrollTop;
      const delta = y - ultimo;
      if (compu.matches || y < 56) setCorrido(false);
      else if (delta > 6) setCorrido(true);
      else if (delta < -6) setCorrido(false);
      if (Math.abs(delta) > 6 || y < 56) ultimo = y;
    };
    main.addEventListener("scroll", alDeslizar, { passive: true });
    return () => main.removeEventListener("scroll", alDeslizar);
  }, [pathname]);

  return { corrido, mostrar: () => setCorrido(false) };
}

function Shell(): JSX.Element {
  const { location } = useRouterState();
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const encabezado = useEncabezadoQueSeCorre(location.pathname);
  const esCelular = useEsCelular();

  useEffect(() => {
    setDrawerAbierto(false);
  }, [location.pathname]);

  /*
    VERSION NUEVA: SE CAMBIA AL CAMBIAR DE PANTALLA
    -----------------------------------------------
    Si hubo un deploy desde que se abrio la pestaña, el proximo cambio de
    pantalla es una recarga completa en vez de una navegacion interna: quien
    usa el sistema ya se estaba yendo de lo que tenia, y asi nunca se queda un
    dia entero con el codigo de la mañana. Mientras tanto, el aviso de arriba
    ofrece hacerlo ya. En medio de una pantalla no se recarga nunca solo.
  */
  const hayVersionNueva = useHayVersionNueva();
  // Mientras la puesta a punto este abierta (0146), cada pantalla lo recuerda.
  const { data: config } = useConfig();
  const faltaPuestaAPunto =
    config?.config != null &&
    config.config.arranque_at === null &&
    location.pathname !== "/puesta-a-punto";
  const [rutaAlDetectar, setRutaAlDetectar] = useState<string | null>(null);
  useEffect(() => {
    if (!hayVersionNueva) return;
    if (rutaAlDetectar === null) setRutaAlDetectar(location.pathname);
    else if (rutaAlDetectar !== location.pathname) window.location.reload();
  }, [hayVersionNueva, location.pathname, rutaAlDetectar]);

  const encabezadoJsx = (
    <>
      {/* `h-14` en los dos: en desktop empata con el bloque del logo del
            sidebar (ver alli), y en el celular es un solo renglón. Antes el
            celular apilaba dos filas (logo y barra del dólar) y se comía
            115 px de 844 en todas las pantallas. En el celular va adentro de
            `<main>` y se corre al bajar (`useEncabezadoQueSeCorre`). */}
      <header
        className={cn(
          "z-30 flex h-14 shrink-0 items-center gap-1 border-b bg-card px-1 md:justify-between md:gap-4 md:px-6",
          esCelular &&
            // `-top-4`: el `sticky` se pega contra el borde del
            // contenido de `<main>`, que tiene 16 px de margen; sin
            // compensarlo asomaba la lista por arriba.
            "sticky -top-4 -mx-4 -mt-4 mb-4 transition-transform duration-200 motion-reduce:transition-none",
        )}
        style={esCelular && encabezado.corrido ? { transform: "translateY(-100%)" } : undefined}
        // Si el foco cae adentro (teclado, lector de pantalla), vuelve.
        onFocusCapture={encabezado.mostrar}
      >
        <div className="flex min-w-0 flex-1 items-center gap-1 md:hidden">
          <DialogPrimitive.Root open={drawerAbierto} onOpenChange={setDrawerAbierto}>
            <DialogPrimitive.Trigger asChild>
              <IconButton type="button" icono={Menu} aria-label="Abrir menu" />
            </DialogPrimitive.Trigger>
            <DialogPrimitive.Portal>
              <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 md:hidden" />
              <DialogPrimitive.Content
                // Sin foco automático: caía en el botón del tema y abría su
                // globito cada vez que se abría el menú.
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r bg-card shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left md:hidden"
              >
                <DialogPrimitive.Title className="sr-only">
                  Menu de navegacion
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="sr-only">
                  Modulos del sistema
                </DialogPrimitive.Description>
                {/* El tema vive acá en el celular, al final de la lista: el
                    encabezado es un solo renglón, y fijo abajo del menú le
                    tapaba el último módulo (Reparaciones). */}
                <CuerpoSidebar
                  pathname={location.pathname}
                  alNavegar={() => setDrawerAbierto(false)}
                  pie={
                    <div className="mt-2 flex items-center justify-between border-t px-2 pt-2 text-sm text-muted-foreground">
                      Tema
                      <ThemeToggle />
                    </div>
                  }
                />
                <DialogPrimitive.Close asChild>
                  {/* 44/40 del primitivo, sin pisarlo: es el cierre del drawer. */}
                  <IconButton
                    type="button"
                    icono={X}
                    aria-label="Cerrar menu"
                    className="absolute right-1 top-1"
                  />
                </DialogPrimitive.Close>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>

          <Link
            to="/"
            aria-label="Ir al mostrador"
            className="flex h-11 shrink-0 items-center rounded-md px-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <LogoManzana className="size-6 text-foreground" />
          </Link>
          {/* Todo en un renglón: el dólar y los contadores en chico, y la
                lupa. El tema se fue al menú: se toca una vez por mes. */}
          <BarraEstado compacto className="ml-auto min-w-0" />
          <BotonBuscar />
        </div>

        {/* El buscador a la izquierda, pegado al contenido. Es lo que mas se
              usa: ponerlo del lado del que se lee primero — y darle el ancho
              que sobra, en vez de dejar un hueco muerto en el medio. */}
        <div className="hidden min-w-0 flex-1 md:block">
          <BotonBuscar />
        </div>

        {/*
            LA COTIZACION VOLVIO AL HEADER, Y ESTA VEZ ANDA
            -----------------------------------------------
            Antes vivio aca una cotizacion compacta que se saco porque leia del
            estado local de MonedaProvider, nunca conectado al backend: mostraba
            "$ 1,0000" con el dolar a 1550. Una cotizacion incorrecta en pantalla
            es peor que ninguna, porque alguien le cotiza a un cliente con ese
            numero. Se la mando entonces a una tarjeta del inicio, que si leia la
            fuente real.

            `BarraEstado` es esa tarjeta, subida de vuelta al header junto con el
            resto de la columna del mostrador — pero conservando la fuente que
            funcionaba (`useConfig`). El motivo del regreso es de espacio: esos
            indicadores le comian 288px de ancho fijo a la venta, que es la
            pantalla que mas se usa, y esta franja ya estaba vacia.

            Lo que NO volvio es el switch global "VER EN ARS/USD". Ese no lo
            consumia ninguna pantalla: todas muestran pesos y dolares a la vez,
            que es lo que hace falta en un negocio bimonetario. Elegir "ver todo
            en dolares" esconde justo la mitad que se necesita.
          */}
        {/*
            El tema se mudo aca desde el pie del sidebar. Abajo ocupaba un
            renglon fijo de la columna de navegacion —el recurso mas escaso de
            esa columna, que tiene que mostrar quince modulos sin scroll— para
            un control que se toca una vez por mes. En el header se suma al
            grupo de interruptores globales que ya estaban (cotizacion,
            discrecion), que es a lo que se parece.
          */}
        <div className="hidden shrink-0 items-center gap-1 md:flex">
          <BarraEstado />
          <ThemeToggle />
        </div>
      </header>
    </>
  );

  const avisosJsx = (
    <>
      {faltaPuestaAPunto && (
        <div className="shrink-0 px-4 pt-3 md:px-6">
          <Aviso tono="warning" titulo="Falta la puesta a punto">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Antes de vender, carguen los clientes que deben y la plata de cada cuenta.
              </span>
              <Button asChild size="sm">
                <Link to="/puesta-a-punto">Ir a la puesta a punto</Link>
              </Button>
            </div>
          </Aviso>
        </div>
      )}
      {hayVersionNueva && (
        <div className="shrink-0 px-4 pt-3 md:px-6">
          <Aviso titulo="Hay una versión nueva de Manzana Mordida">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Simplika sumó mejoras. Actualizá cuando termines lo que estás cargando: lo que no
                guardaste se pierde.
              </span>
              <Button size="sm" onClick={() => window.location.reload()}>
                ACTUALIZA
              </Button>
            </div>
          </Aviso>
        </div>
      )}
    </>
  );

  return (
    /*
      EL DOCUMENTO NO SCROLLEA. NUNCA.
      --------------------------------
      El shell mide exactamente una pantalla y se planta: lo que sobra
      scrollea adentro, en la lista de modulos del sidebar y en `main`. De ahi
      sale lo que se pidio dos veces — que el mostrador entre de una, y que el
      header con el buscador y la cotizacion quede clavado arriba en vez de
      irse con el scroll.

      `100dvh` y no `100vh`: el `dvh` es el alto de viewport que YA descuenta
      las barras del navegador, y se recalcula cuando aparecen o desaparecen.
      Con `100vh` el celular se come el ultimo renglon, que es el motivo por
      el que esto antes se limitaba a desktop y el header seguia suelto en
      todo lo demas — ventana angosta, zoom del navegador, telefono.
    */
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-background text-foreground md:flex-row">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg"
      >
        Saltar al contenido
      </a>

      {/* Sidebar fijo, md+ */}
      <aside className="hidden w-64 shrink-0 flex-col border-r bg-card md:flex">
        <CuerpoSidebar pathname={location.pathname} />
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/*
          Header. El switch ARS/USD y la cotizacion viven aca y son visibles TAMBIEN
          en mobile: es la informacion que mas se consulta y la que mas caro sale
          suponer.
        */}
        {esCelular ? null : encabezadoJsx}

        {/*
          `min-h-0` es lo que hace que esto scrollee en vez de estirarse: sin
          el, un hijo flex no baja de su alto de contenido y el desborde se lo
          come el shell, que tiene `overflow-hidden`.
        */}
        {esCelular ? null : avisosJsx}

        {/*
          `relative` hace falta: los textos solo para lector de pantalla
          (`sr-only`, que es `position: absolute`) se ubicaban respecto de la
          página y no de esta zona que se desliza. En Caja y en la ficha del
          cliente estiraban el documento a casi 4.000 px: al deslizar en el
          celular se iba el encabezado y abajo quedaba blanco.
        */}
        <main
          id="contenido"
          className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6"
        >
          {esCelular ? encabezadoJsx : null}
          {esCelular ? avisosJsx : null}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: () => (
    <TooltipProvider delayDuration={300} skipDelayDuration={150}>
      {/* `RequiereSesion` decide entre TRES destinos, no dos: mientras carga no
          mueve a nadie (redirigir antes de saber quien sos manda al login a
          alguien con la sesion valida), sin usuarios manda a /instalar, y sin
          sesion manda a /login. Deja pasar las rutas publicas para que el propio
          login no se redirija a si mismo. */}
      <RequiereSesion publica={<PantallaSuelta />}>
        {/* El buscador se monta UNA vez, arriba del Outlet: asi se abre desde
            cualquier pantalla sin navegar, que es todo el punto. */}
        <BuscadorProvider>
          <Shell />
        </BuscadorProvider>
      </RequiereSesion>
      <VentanaPausa />
      <Toaster />
    </TooltipProvider>
  ),
  notFoundComponent: () => (
    <EstadoVacio
      icono={Smartphone}
      titulo="No encontramos esa pantalla"
      detalle="Puede que el modulo todavia no exista o que el link este viejo."
      accion={
        <Button asChild variant="outline">
          <Link to="/">Volver al mostrador</Link>
        </Button>
      }
      className="mx-auto max-w-xl"
    />
  ),
});
